import { useState, useEffect, useCallback, useRef } from 'react';
import {
  createHold,
  extendHold,
  releaseHold,
  validateHold,
  getUserHolds,
} from '../services/bookingHoldsService';

/**
 * Custom hook for managing booking holds
 * @param {Object} options - Hook options
 * @param {string} options.guideId - Guide ID
 * @param {string} options.guideServiceId - Guide service ID
 * @returns {Object} Hold management functions and state
 */
export function useBookingHold({ guideId, guideServiceId }) {
  const [hold, setHold] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [timeRemaining, setTimeRemaining] = useState(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showStillInterestedModal, setShowStillInterestedModal] = useState(false);
  const [showExpiredModal, setShowExpiredModal] = useState(false);
  const [holdRestored, setHoldRestored] = useState(false);

  const intervalRef = useRef(null);
  const extendTimeoutRef = useRef(null);
  const stillInterestedShownRef = useRef(false);
  const expiredShownRef = useRef(false);

  // Calculate time remaining for hold expiration
  const calculateTimeRemaining = useCallback(() => {
    if (!hold?.data?.expires_at) {
      setTimeRemaining(null);
      return;
    }

    const now = new Date();
    const expiresAt = new Date(hold.data.expires_at);
    const diff = expiresAt - now;

    if (diff <= 0) {
      // Hold has expired - show final expiration modal
      if (!expiredShownRef.current) {
        setShowExpiredModal(true);
        expiredShownRef.current = true;
      }
      setTimeRemaining(null);
      setHold(null);
      return;
    }

    const minutes = Math.floor(diff / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);
    setTimeRemaining({ minutes, seconds, total: diff });

    // Show "Still Interested?" modal at ~5 minutes remaining (unless already shown)
    // This gives user time to see modal and decide before final expiration
    if (minutes < 5 && !stillInterestedShownRef.current) {
      setShowStillInterestedModal(true);
      stillInterestedShownRef.current = true;
    }
  }, [hold]);

  // Start countdown timer
  useEffect(() => {
    if (hold?.data?.expires_at) {
      calculateTimeRemaining();
      intervalRef.current = setInterval(calculateTimeRemaining, 1000);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [hold, calculateTimeRemaining]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (extendTimeoutRef.current) {
        clearTimeout(extendTimeoutRef.current);
      }
    };
  }, []);

  // Restore active hold from session on mount
  useEffect(() => {
    async function restoreHold() {
      if (holdRestored) {return;} // Already restored

      try {
        setLoading(true);
        const result = await getUserHolds();

        if (result.success && result.data && result.data.length > 0) {
          // Find hold matching this guide and service
          const matchingHold = result.data.find(
            h => h.guide_id === guideId && h.guide_service_id === guideServiceId
          );

          if (matchingHold) {
            // Check if hold is still valid (not expired)
            const expiresAt = new Date(matchingHold.expires_at);
            const now = new Date();

            if (expiresAt > now) {
              // Hold is still valid, restore it
              setHold({ success: true, data: matchingHold });
              setHoldRestored(true);
            } else {
              // Hold expired, don't restore
              setHoldRestored(true);
            }
          } else {
            // No matching hold found
            setHoldRestored(true);
          }
        } else {
          // No holds found
          setHoldRestored(true);
        }
      } catch (err) {
        console.error('Failed to restore hold:', err);
        setHoldRestored(true); // Mark as attempted even on error
      } finally {
        setLoading(false);
      }
    }

    restoreHold();
  }, [guideId, guideServiceId, holdRestored]);

  /**
   * Create a new booking hold
   * @param {string} date - Date in YYYY-MM-DD format
   */
  const createBookingHold = useCallback(
    async (date) => {
      setLoading(true);
      setError(null);
      setSuggestions([]);

      // Reset modal states for new hold
      stillInterestedShownRef.current = false;
      expiredShownRef.current = false;
      setShowStillInterestedModal(false);
      setShowExpiredModal(false);

      try {
        const result = await createHold({
          guide_id: guideId,
          experience_id: guideServiceId,
          date,
        });

        if (result.success) {
          setHold(result);
          return { success: true, hold: result.data };
        } else {
          // Handle race condition with alternative dates
          setError(result.message || 'Failed to create hold');
          if (result.suggestions) {
            setSuggestions(result.suggestions);
          }
          return { success: false, error: result.error, suggestions: result.suggestions };
        }
      } catch (err) {
        setError(err.message || 'Failed to create hold');
        return { success: false, error: err.message };
      } finally {
        setLoading(false);
      }
    },
    [guideId, guideServiceId]
  );

  /**
   * Extend the current hold
   * @param {number} checkoutStep - Current checkout step
   * @param {string} actionType - Action type
   */
  const extendBookingHold = useCallback(
    async (checkoutStep, actionType) => {
      if (!hold?.data?.id) {return;}

      try {
        const result = await extendHold(hold.data.id, checkoutStep, actionType);
        if (result.success) {
          setHold({ success: true, data: result.data });
        }
      } catch (err) {
        console.error('Failed to extend hold:', err);
      }
    },
    [hold]
  );

  /**
   * Release the current hold
   */
  const releaseBookingHold = useCallback(async () => {
    if (!hold?.data?.id) {return;}

    try {
      await releaseHold(hold.data.id);
      setHold(null);
      setTimeRemaining(null);
      setSuggestions([]);
    } catch (err) {
      console.error('Failed to release hold:', err);
    }
  }, [hold]);

  /**
   * Validate the current hold
   */
  const validateBookingHold = useCallback(async () => {
    if (!hold?.data?.id) {return false;}

    try {
      const result = await validateHold(hold.data.id);
      if (!result.valid) {
        setHold(null);
        setTimeRemaining(null);
        // Use user-friendly message from API or fallback
        setError(result.message || result.reason || 'Hold is no longer valid');
        return false;
      }
      return true;
    } catch (err) {
      console.error('Failed to validate hold:', err);
      setError(err.message || 'Unable to validate hold');
      return false;
    }
  }, [hold]);

  /**
   * Auto-extend hold when user takes meaningful actions
   */
  const trackProgressAction = useCallback(
    (checkoutStep, actionType) => {
      // Debounce extension requests
      if (extendTimeoutRef.current) {
        clearTimeout(extendTimeoutRef.current);
      }

      extendTimeoutRef.current = setTimeout(() => {
        extendBookingHold(checkoutStep, actionType);
      }, 500);
    },
    [extendBookingHold]
  );

  /**
   * Handle "Still Interested?" modal extension
   * Extends hold by 5 minutes
   */
  const handleExtendFromModal = useCallback(async () => {
    setShowStillInterestedModal(false);
    if (hold?.data?.id) {
      await extendBookingHold(1, 'modal_extension');
    }
  }, [hold, extendBookingHold]);

  /**
   * Handle hold release from "Still Interested?" modal
   */
  const handleReleaseFromModal = useCallback(() => {
    setShowStillInterestedModal(false);
    releaseBookingHold();
  }, [releaseBookingHold]);

  /**
   * Handle reload from expired modal
   */
  const handleReloadFromExpired = useCallback(() => {
    setShowExpiredModal(false);
    window.location.reload();
  }, []);

  /**
   * Handle continue browsing from expired modal
   */
  const handleContinueBrowsing = useCallback(() => {
    setShowExpiredModal(false);
    // Hold is already null at this point
  }, []);

  return {
    hold: hold?.data || null,
    loading,
    error,
    timeRemaining,
    suggestions,
    createHold: createBookingHold,
    extendHold: extendBookingHold,
    releaseHold: releaseBookingHold,
    validateHold: validateBookingHold,
    trackProgressAction,
    // Modal state and handlers
    showStillInterestedModal,
    showExpiredModal,
    handleExtendFromModal,
    handleReleaseFromModal,
    handleReloadFromExpired,
    handleContinueBrowsing,
  };
}
